import React from 'react';
import { Col, Row, Typography } from 'antd';
import {
  ContentContainer,
  DateWithoutTimeZone,
  InfoTooltip,
  TextOverflow,
} from '@common/components';
import { now } from 'works/legacy/lib/date-helper';
import { IncentiveRatesView } from '@scheduling/components';
import {
  UnitRateType,
  SimplifiedWorkforceGroupType,
} from '@scheduling/graphql';
import { OrganizationUnitType } from '@institutions/graphql';
import { Moment } from 'moment';

const { Title } = Typography;

function joinAllRecordNames(
  result: string,
  object: SimplifiedWorkforceGroupType | OrganizationUnitType,
  i: number
) {
  const isFirstUnit = i === 0;

  return isFirstUnit ? object.name : `${result}, ${object.name}`;
}

function labelPlural(
  objectList: SimplifiedWorkforceGroupType[] | { name: string }[]
) {
  const isSingle = objectList.length === 1;

  return isSingle ? '' : 's';
}

type IncentivesUpdateConfirmationStepProps = {
  selectedWorkforceGroups: SimplifiedWorkforceGroupType[];
  effectiveDate: string | Moment;
  endDate: string | Moment;
  selectedOrgsUnits: { id: string; name: string }[];
  ratesValues: UnitRateType;
};

export function IncentivesUpdateConfirmationStep({
  selectedWorkforceGroups,
  effectiveDate,
  endDate,
  ratesValues,
  selectedOrgsUnits,
}: IncentivesUpdateConfirmationStepProps) {
  const unitsNames = selectedOrgsUnits.reduce(joinAllRecordNames, '');
  const wfgNames = selectedWorkforceGroups.reduce(joinAllRecordNames, '');
  const unitNamesLabel = `Unit Name${labelPlural(selectedOrgsUnits)}`;
  const wfgNamesLabel = `Workforce Group${labelPlural(
    selectedWorkforceGroups
  )}`;

  return (
    <>
      <div className="confirm-rates-updates">
        <ContentContainer
          header={
            <>
              <Row gutter={24}>
                <Col span={24}>
                  <Title level={3}>Summary</Title>
                </Col>
                <Col span={4}>
                  <IncentiveRatesView.Label label="Effective Date" />
                  <InfoTooltip
                    size="sm"
                    leftOffset={8}
                    overlayStyle={{ maxWidth: 300 }}
                    title="These updates will take effect on this date."
                  />
                </Col>
                <Col span={20}>
                  <span>
                    <DateWithoutTimeZone date={effectiveDate || now()} />
                  </span>
                </Col>
              </Row>
              {endDate && (
                <Row gutter={24}>
                  <Col span={4}>
                    <IncentiveRatesView.Label label="End Date" />
                    <InfoTooltip
                      size="sm"
                      leftOffset={8}
                      overlayStyle={{ maxWidth: 300 }}
                      title="After the selected end date, rates will return to the previously active rate card."
                    />
                  </Col>
                  <Col span={20}>
                    <span>
                      <DateWithoutTimeZone date={endDate || ''} />
                    </span>
                  </Col>
                </Row>
              )}
              <Row gutter={24}>
                <Col span={4}>
                  <IncentiveRatesView.Label label={wfgNamesLabel} />
                </Col>
                <Col span={20}>
                  <div
                    key="wfgNames"
                    className="confirm-rates-updates__units-names"
                  >
                    <TextOverflow>{wfgNames}</TextOverflow>
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={4}>
                  <IncentiveRatesView.Label label={unitNamesLabel} />
                </Col>
                <Col span={20}>
                  <div
                    key="unitsNames"
                    className="confirm-rates-updates__units-names"
                  >
                    <TextOverflow>{unitsNames}</TextOverflow>
                  </div>
                </Col>
              </Row>
            </>
          }
        >
          <div className="confirm-rates-updates__body">
            <Row gutter={[0, 36]}>
              <Col span={24}>
                <Title level={3}>Dynamic Pricing Settings</Title>
                <IncentiveRatesView.Dynamic rates={ratesValues} />
              </Col>
              <Col span={24}>
                <Title level={3}>Incentive Rates</Title>
                <IncentiveRatesView.Variable rates={ratesValues} />
              </Col>
            </Row>
          </div>
        </ContentContainer>
      </div>
      <style jsx>{`
        .confirm-rates-updates__effective-date-container {
          margin-bottom: 10px;
        }
        .confirm-rates-updates :global(.confirm-rates-updates__units-names) {
          width: calc(100vw - 380px);
        }
      `}</style>
    </>
  );
}
